import { HowToReg, NotificationImportant, PeopleAlt, Person } from '@mui/icons-material';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { KeyDriveSearchBarWithButton } from '../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBarWithButton';
import { GroupsTable } from './components/GroupsTable/GroupsTable';
import { PrivilegesTable } from './components/PrivilegesTable/PrivilegesTable';
import UsersTable from './components/UsersTable/UsersTable';
import NotificationGuestTable from './components/NotificationTable/NotificationTable';
import useExtractPlan from '../../../hooks/useExtractPlan';
import { KeydrivePlan } from '../../../model/UserModel';
import { verifyUsers } from '../../../services/paywallService';
import { PaywallInformation } from '../../../model/PaywallModel';
import { PaywallModal } from '../../../components/PaywallModal/PaywallModal';
import { MaxUsersIcon } from '../../../assets/icons/MaxUsersIcon';
import { useProfile } from '../../../context/ProfileProvider/useProfile';
import { KeyDriveSearchBar } from '../../../components/KeydriveComponents/SearchBars/KeyDriveSearchBar';
import { KeyDriveBodyMin } from '../../../components/KeydriveComponents/Fonts/Body/KeyDriveBodyMin';
import { KeyDrivePrimaryButton } from '../../../components/KeydriveComponents/Buttons/KeyDrivePrimaryButton';
import { useEffect } from 'react';

interface StyledTabProps {
  icon?: string | React.ReactElement;
  iconPosition?: "bottom" | "top" | "end" | "start" | undefined;
  label?: React.ReactNode;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const AntTab = styled((props: StyledTabProps) => <Tab iconPosition="start" disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    color: "#4E82A4",
    font: 'normal normal 600 14px/27px Segoe UI',
    minHeight: '34px',
    '&:hover': {
      backgroundColor: '#E8EFF3',
    },
    '&.Mui-selected': {
      color: '#22638D',
    },

  }),
);

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function UsersAndGroupsTab() {
  const [value, setValue] = React.useState(0);
  const { nameOrg } = useParams();
  const [searchUserName, setSearchUserName] = React.useState<string>('');
  const [searchGroupName, setSearchGroupName] = React.useState<string>('');
  const { keydrivePlan } = useExtractPlan();
  const { t } = useTranslation();
  const [paywallInfo, setPaywallInfo] = React.useState<PaywallInformation>();
  const { planName } = useExtractPlan();
  const [paywallOpen, setPaywallOpen] = React.useState<boolean>(false);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const profile = useProfile();
  const onChangeUserName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value != undefined) {
      setSearchUserName(event.target.value);
    }
  }

  const onChangeGroupName = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value != undefined) {
      setSearchGroupName(event.target.value);
    }
  }

  const navigate = useNavigate();
  
  const location = useLocation();

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  useEffect(() => {
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab")?.toLocaleLowerCase();

  switch (tab) {
    case "users":
      setValue(0);
      break;
    case "groups":
      setValue(1);
      break;
    case "privileges":
      setValue(2);
      break;
    case "migrations":
      setValue(3);
      break;
    default:
      setValue(0);
      break;
  }
}, [location.search]);

  if (profile.adminSystem) {
    return (
          <Box sx={{ width: '100%', height: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                <AntTab icon={<Person />} label={t('AdminConsole.Users')} {...a11yProps(0)} />
                <AntTab icon={<PeopleAlt />} label={t('AdminConsole.Groups')} {...a11yProps(1)} />
                <AntTab icon={<HowToReg />} label={t('AdminConsole.Privileges')} {...a11yProps(2)} />
                <AntTab icon={<NotificationImportant />} label={t("MigrationsTab.Migrations")} {...a11yProps(3)} />
              </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <KeyDriveSearchBar
                onChange={onChangeUserName}
              />
              <Box marginTop="10px" display="flex" gap={1}>
                <KeyDrivePrimaryButton 
                    TextComponent={KeyDriveBodyMin}
                    TextComponentProps={{
                        color: "",
                        needBold: false,
                        needEllipses: false,
                        text: t('AdminConsole.NewUser'),
                    }}
                    onClick={() => {
                      navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser`)
                    }}
                />
                <KeyDrivePrimaryButton 
                    TextComponent={KeyDriveBodyMin}
                    TextComponentProps={{
                        color: "",
                        needBold: false,
                        needEllipses: false,
                        text: t('AdminConsole.GuestUserAdd'),
                    }}
                    onClick={() => {
                      navigate(`/${nameOrg}/adminConsole/usersAndGroups/newGuestUser`)
                    }}
                />
              </Box>
              <div style={{ marginTop: "20px" }}>
                <UsersTable isGroupPage={false} nameOrg={nameOrg!} search={searchUserName} />
              </div>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <KeyDriveSearchBarWithButton onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newGroup`)} btnText={t('AdminConsole.NewGroup')} onChange={onChangeGroupName} />
              <div style={{ marginTop: "20px" }}>
                <GroupsTable isGroupTable nameOrg={nameOrg!} search={searchGroupName} />
              </div>
            </CustomTabPanel>
              <CustomTabPanel value={value} index={2}>
                <PrivilegesTable />
              </CustomTabPanel>
              <CustomTabPanel value={value} index={3}>
                <NotificationGuestTable />
              </CustomTabPanel>
            <PaywallModal
                icon={<MaxUsersIcon />}
                open={paywallOpen}
                setOpen={setPaywallOpen}
                subtitleStart={paywallInfo?.paywallContent!}
                subtitleBold={` ${planName}`}
                subtitleEnd={paywallInfo?.paywallTitle!}
                paywallContent={paywallInfo?.paywallContent!}
            />
          </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <AntTab icon={<Person />} label={t('AdminConsole.Users')} {...a11yProps(0)} />
          { keydrivePlan !== KeydrivePlan.FREE && !profile.adminSystem && (
            <AntTab icon={<PeopleAlt />} label={t('AdminConsole.Groups')} {...a11yProps(1)} />
          ) }
          { keydrivePlan !== KeydrivePlan.FREE && !profile.adminSystem && (
            <AntTab icon={<HowToReg />} label={t('AdminConsole.Privileges')} {...a11yProps(2)} />
          )}
          { keydrivePlan !== KeydrivePlan.FREE && !profile.adminSystem && (
            <AntTab icon={<NotificationImportant />} label={t("MigrationsTab.Migrations")} {...a11yProps(3)} />
          ) }
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <KeyDriveSearchBar onChange={onChangeUserName}/>
        <Box marginTop="10px" display="flex" gap={1}>
          <KeyDrivePrimaryButton 
              TextComponent={KeyDriveBodyMin}
              TextComponentProps={{
                  color: "",
                  needBold: false,
                  needEllipses: false,
                  text: t('AdminConsole.NewUser'),
              }}
              onClick={() => {
                  verifyUsers(nameOrg!)
                .then(data => {
                  if (!data.canDo) {
                    setPaywallInfo(data);
                    setPaywallOpen(true);
                  } else {
                    navigate(`/${nameOrg}/adminConsole/usersAndGroups/newUser`)
                  }
                })
                .catch()
              }}
          />
          { profile?.signtureInformation?.currentPlan != KeydrivePlan.FREE && (
            <KeyDrivePrimaryButton 
                TextComponent={KeyDriveBodyMin}
                TextComponentProps={{
                    color: "",
                    needBold: false,
                    needEllipses: false,
                    text: t('AdminConsole.GuestUserAdd'),
                }}
                onClick={() => {
                    verifyUsers(nameOrg!)
                  .then(data => {
                    if (!data.canDo) {
                      setPaywallInfo(data);
                      setPaywallOpen(true);
                    } else {
                      navigate(`/${nameOrg}/adminConsole/usersAndGroups/newGuestUser`)
                    }
                  })
                  .catch()
                }}
            />
          ) }
        </Box>
        <div style={{ marginTop: "20px" }}>
          <UsersTable isGroupPage={false} nameOrg={nameOrg!} search={searchUserName} />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <KeyDriveSearchBarWithButton onClick={() => navigate(`/${nameOrg}/adminConsole/usersAndGroups/newGroup`)} btnText={t('AdminConsole.NewGroup')} onChange={onChangeGroupName} />
        <div style={{ marginTop: "20px" }}>
          <GroupsTable isGroupTable nameOrg={nameOrg!} search={searchGroupName} />
        </div>
      </CustomTabPanel>
      { keydrivePlan != KeydrivePlan.FREE && !profile.adminSystem && (
        <CustomTabPanel value={value} index={2}>
          <PrivilegesTable />
        </CustomTabPanel>
      ) }
      { keydrivePlan != KeydrivePlan.FREE && !profile.adminSystem && (
        <CustomTabPanel value={value} index={3}>
          <NotificationGuestTable />
        </CustomTabPanel>
      ) }
      <PaywallModal
          icon={<MaxUsersIcon />}
          open={paywallOpen}
          setOpen={setPaywallOpen}
          subtitleStart={paywallInfo?.paywallContent!}
          subtitleBold={` ${planName}`}
          subtitleEnd={paywallInfo?.paywallTitle!}
          paywallContent={paywallInfo?.paywallContent!}
      />
    </Box>
  );
}